import { Copy, Download, Edit3, Plus, Save, Trash2, Upload } from "lucide-react";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, api } from "../lib/api";
import type { CardPayload, TemplateItem } from "../lib/types";

type PresetTemplate = {
  id: string;
  name: string;
  description: string;
  payload: CardPayload;
};

const CURRENT_CARD_KEY = "telegram-card-current-payload";
const LOAD_CARD_KEY = "telegram-card-edit-payload";

function button(text: string, url: string) {
  return {
    id: crypto.randomUUID(),
    text,
    url
  };
}

function makePayload(partial: Pick<CardPayload, "title" | "caption" | "buttons" | "buttonLayout">): CardPayload {
  return {
    channelId: "",
    mediaType: "photo",
    ...partial
  };
}

const presetTemplates: PresetTemplate[] = [
  {
    id: "general-news",
    name: "ข่าวสารทั่วไป",
    description: "โพสต์ข่าวสารหรืออัปเดตประจำวันที่อ่านง่าย",
    payload: makePayload({
      title: "ข่าวสารจากทีมงาน",
      caption: "📢 ข่าวสารจากทีมงาน\n\nอัปเดตข้อมูลล่าสุดสำหรับสมาชิกทุกท่าน\n\nติดตามรายละเอียดเพิ่มเติมได้ที่ปุ่มด้านล่าง",
      buttons: [button("อ่านรายละเอียด", "https://example.com/news"), button("เข้าช่อง Telegram", "https://t.me/example")],
      buttonLayout: 2
    })
  },
  {
    id: "promotion",
    name: "โปรโมชั่น",
    description: "ประกาศโปรโมชันพร้อมปุ่มสมัครและติดต่อ",
    payload: makePayload({
      title: "โปรโมชั่นพิเศษ",
      caption: "🎁 โปรโมชั่นพิเศษวันนี้\n\nรับข้อเสนอสุดคุ้มสำหรับสมาชิกใหม่และสมาชิกปัจจุบัน\n\nกดปุ่มด้านล่างเพื่อดูรายละเอียด",
      buttons: [button("สมัครสมาชิก", "https://example.com/register"), button("ดูโปรโมชั่น", "https://example.com/promo")],
      buttonLayout: 2
    })
  },
  {
    id: "activity",
    name: "แจ้งกิจกรรม",
    description: "เชิญชวนเข้าร่วมกิจกรรมของคอมมูนิตี้",
    payload: makePayload({
      title: "กิจกรรมประจำสัปดาห์",
      caption: "🔥 กิจกรรมประจำสัปดาห์เริ่มแล้ว\n\nร่วมสนุกกับทีมงานและลุ้นรับรางวัลพิเศษ\n\nอย่าลืมอ่านกติกาก่อนเข้าร่วมกิจกรรม",
      buttons: [button("ดูรายละเอียดกิจกรรม", "https://example.com/event"), button("ติดต่อแอดมิน", "https://t.me/admin")],
      buttonLayout: 1
    })
  },
  {
    id: "welcome",
    name: "ต้อนรับสมาชิก",
    description: "ข้อความต้อนรับสมาชิกใหม่พร้อมช่องทางเริ่มต้น",
    payload: makePayload({
      title: "ยินดีต้อนรับสมาชิกใหม่",
      caption: "👑 ยินดีต้อนรับสมาชิกใหม่ทุกท่าน\n\nขอให้สนุกกับคอมมูนิตี้ของเรา หากต้องการความช่วยเหลือสามารถติดต่อทีมงานได้ทันที",
      buttons: [button("เริ่มต้นใช้งาน", "https://example.com/start"), button("ติดต่อทีมงาน", "https://t.me/admin")],
      buttonLayout: 2
    })
  },
  {
    id: "important",
    name: "ประกาศสำคัญ",
    description: "ประกาศที่ต้องการให้สมาชิกเห็นชัดและอ่านทันที",
    payload: makePayload({
      title: "ประกาศสำคัญ",
      caption: "✅ ประกาศสำคัญจากทีมงาน\n\nกรุณาอ่านรายละเอียดนี้ให้ครบถ้วน เพื่อให้การใช้งานเป็นไปอย่างราบรื่น\n\nขอบคุณสมาชิกทุกท่าน",
      buttons: [button("อ่านประกาศ", "https://example.com/announcement")],
      buttonLayout: 1
    })
  }
];

export function Templates() {
  const navigate = useNavigate();
  const [items, setItems] = useState<TemplateItem[]>([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const sortedItems = useMemo(() => items.slice().sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)), [items]);

  const load = async () => {
    try {
      const result = await api.templates();
      setItems(result.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "โหลด Template ไม่สำเร็จ");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const loadTemplate = (template: Pick<TemplateItem, "payload">) => {
    localStorage.setItem(LOAD_CARD_KEY, JSON.stringify(template.payload));
    navigate("/create");
  };

  const duplicate = async (template: Pick<TemplateItem, "name" | "payload">) => {
    try {
      await api.saveTemplate({ name: `${template.name} copy`, payload: template.payload });
      setNotice("Duplicate Template แล้ว");
      setError("");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Duplicate Template ไม่สำเร็จ");
    }
  };

  const saveCurrentCard = async () => {
    const rawPayload = localStorage.getItem(CURRENT_CARD_KEY);
    if (!rawPayload) {
      setError("ยังไม่มี current card ให้บันทึก กรุณาไปหน้า Create Card แล้วแก้ไขการ์ดก่อน");
      return;
    }

    try {
      const payload = JSON.parse(rawPayload) as CardPayload;
      const name = window.prompt("ชื่อ Template", payload.title || "Current card template");
      if (!name) return;
      await api.saveTemplate({ name, payload });
      setNotice("บันทึก Current Card เป็น Template แล้ว");
      setError("");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "บันทึก Current Card ไม่สำเร็จ");
    }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(items, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "telegram-card-templates.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const importJson = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const templates = Array.isArray(parsed) ? parsed : [parsed];
      for (const template of templates) {
        if (template?.payload) await api.saveTemplate({ name: template.name ?? "Imported template", payload: template.payload });
      }
      setNotice("Import Template สำเร็จ");
      setError("");
      load();
    } catch {
      setError("ไฟล์ JSON ไม่ถูกต้อง");
    } finally {
      event.target.value = "";
    }
  };

  const removeTemplate = async (id: string) => {
    if (!window.confirm("ลบ Template นี้ใช่ไหม?")) return;
    await api.deleteTemplate(id);
    setNotice("ลบ Template แล้ว");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Templates</h1>
          <p className="mt-1 text-sm text-slate-500">เลือกข้อความสำเร็จรูป โหลดเข้า Create Card หรือบันทึกการ์ดปัจจุบันไว้ใช้ซ้ำ</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={saveCurrentCard}
            className="inline-flex items-center gap-2 rounded bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-teal-800"
          >
            <Save size={16} />
            Save Current Card as Template
          </button>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded border border-line bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100">
            <Upload size={16} />
            Import
            <input type="file" accept="application/json" className="sr-only" onChange={importJson} />
          </label>
          <button onClick={exportJson} className="inline-flex items-center gap-2 rounded bg-ink px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700">
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {notice ? <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</div> : null}
      {error ? <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div> : null}

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-ink">Preset Templates</h2>
          <p className="mt-1 text-sm text-slate-500">ข่าวสารทั่วไป, โปรโมชั่น, แจ้งกิจกรรม, ต้อนรับสมาชิก และประกาศสำคัญ</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {presetTemplates.map((template) => (
            <article key={template.id} className="rounded border border-line bg-white p-4 shadow-sm">
              <h3 className="truncate text-base font-semibold text-ink">{template.name}</h3>
              <p className="mt-1 text-sm leading-5 text-slate-500">{template.description}</p>
              <p className="mt-3 line-clamp-3 min-h-16 whitespace-pre-wrap text-sm leading-5 text-slate-600">{template.payload.caption}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => loadTemplate(template)} className="inline-flex items-center gap-1 rounded border border-line px-2.5 py-1.5 text-sm hover:bg-slate-100">
                  <Edit3 size={15} />
                  Load Template
                </button>
                <button onClick={() => duplicate(template)} className="inline-flex items-center gap-1 rounded border border-line px-2.5 py-1.5 text-sm hover:bg-slate-100">
                  <Plus size={15} />
                  Save Copy
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-ink">Saved Templates</h2>
          <p className="mt-1 text-sm text-slate-500">Templates ที่บันทึกไว้ในระบบ</p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {sortedItems.map((template) => (
            <article key={template.id} className="rounded border border-line bg-white p-4 shadow-sm">
              <h3 className="truncate text-base font-semibold text-ink">{template.name}</h3>
              <p className="mt-1 line-clamp-3 min-h-16 whitespace-pre-wrap text-sm leading-5 text-slate-500">{template.payload.caption || "No caption"}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => loadTemplate(template)} className="inline-flex items-center gap-1 rounded border border-line px-2.5 py-1.5 text-sm hover:bg-slate-100">
                  <Edit3 size={15} />
                  Load Template
                </button>
                <button onClick={() => duplicate(template)} className="inline-flex items-center gap-1 rounded border border-line px-2.5 py-1.5 text-sm hover:bg-slate-100">
                  <Copy size={15} />
                  Duplicate Template
                </button>
                <button
                  onClick={() => removeTemplate(template.id)}
                  className="inline-flex items-center gap-1 rounded border border-red-200 px-2.5 py-1.5 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={15} />
                  Delete Template
                </button>
              </div>
            </article>
          ))}
        </div>
        {sortedItems.length === 0 ? <div className="rounded border border-line bg-white p-6 text-sm text-slate-500">ยังไม่มี Template ที่บันทึกไว้</div> : null}
      </section>
    </div>
  );
}
